import { useState, useRef, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useOpportunity, useDeepDiveMessages } from "@/hooks/use-mock-data";
import type { DeepDiveMessage } from "@/hooks/use-mock-data";
import { api } from "@/lib/api";
import { ScoreBadge } from "@/components/strangelove/ScoreBadge";
import { TierBadge } from "@/components/strangelove/TierBadge";
import { SaturationBadge } from "@/components/strangelove/SaturationBadge";
import { EvidencePanel } from "@/components/strangelove/EvidencePanel";
import { DeepDiveSections } from "@/components/strangelove/DeepDiveSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import {
  ArrowLeft, Send, MessageSquare, Shield, Eye, Zap, Calendar,
  Loader2, Waves, FileText, BarChart,
  Target, MessageCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";

const STRATEGIC_PROMPTS = [
  { label: "Hormozi Value Equation", icon: Zap },
  { label: "Thiel's 7 Questions", icon: Eye },
  { label: "Blue Ocean Canvas", icon: Waves },
  { label: "Blind Spot Analysis", icon: Shield },
];

const ACTION_PROMPTS = [
  { label: "Generate PRD", icon: FileText },
  { label: "Pitch Deck Outline", icon: BarChart },
  { label: "Find First 10 Customers", icon: Target },
  { label: "Write Cold Outreach", icon: MessageCircle },
  { label: "Generate Monday Plan", icon: Calendar },
];

const MARKET_METRICS = [
  { key: "painIntensity", label: "PAIN INTENSITY", weight: "15%" },
  { key: "purchasingPower", label: "PURCHASING POWER", weight: "10%" },
  { key: "targetability", label: "TARGETABILITY", weight: "10%" },
  { key: "marketGrowth", label: "MARKET GROWTH", weight: "10%" },
  { key: "whitespace", label: "WHITESPACE", weight: "10%" },
  { key: "monopolyPotential", label: "MONOPOLY POTENTIAL", weight: "5%" },
] as const;

const ADVANTAGE_METRICS = [
  { key: "domainProximity", label: "DOMAIN PROXIMITY", weight: "10%" },
  { key: "distributionAdvantage", label: "DISTRIBUTION ADVANTAGE", weight: "10%" },
  { key: "buildFeasibility", label: "BUILD FEASIBILITY", weight: "10%" },
  { key: "speedToRevenue", label: "SPEED TO REVENUE", weight: "10%" },
] as const;

export default function DeepDivePage() {
  const { id } = useParams();
  const { data: opportunity } = useOpportunity(id ?? "");
  const { data: serverMessages } = useDeepDiveMessages(id ?? "");
  const [localMessages, setLocalMessages] = useState<DeepDiveMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [sending, setSending] = useState(false);
  const [editingMetric, setEditingMetric] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (serverMessages.length > 0) {
      setLocalMessages(serverMessages);
    }
  }, [serverMessages]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [localMessages]);

  if (!opportunity) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[50vh]">
        <p className="text-muted-foreground">Opportunity not found, Commander.</p>
        <Link to="/opportunities" className="text-electric-blue text-sm mt-2 hover:underline">
          Return to Opportunities
        </Link>
      </div>
    );
  }

  const handleSend = async (overrideContent?: string) => {
    const content = overrideContent ?? chatInput.trim();
    if (!content || sending) return;
    setChatInput("");
    setSending(true);

    const tempCommanderMsg: DeepDiveMessage = {
      id: `temp-${Date.now()}`,
      opportunityId: id!,
      role: "commander",
      content,
      createdAt: new Date().toISOString(),
    };
    setLocalMessages((prev) => [...prev, tempCommanderMsg]);

    try {
      const result = await api.post<{
        commander: DeepDiveMessage;
        strangelove: DeepDiveMessage;
      }>(`/api/opportunities/${id}/messages`, { content });

      setLocalMessages((prev) => [
        ...prev.filter((m) => m.id !== tempCommanderMsg.id),
        result.commander,
        result.strangelove,
      ]);
    } catch {
      setLocalMessages((prev) =>
        prev.filter((m) => m.id !== tempCommanderMsg.id)
      );
    } finally {
      setSending(false);
    }
  };

  const displayName = opportunity.opportunityName || opportunity.title;
  const competitors = opportunity.existingCompetitors || [];

  const renderScoreBar = (value: number, color: string) => (
    <div className="flex items-center gap-2 flex-1">
      <div className="flex-1 h-1.5 bg-[rgba(255,255,255,0.06)] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${value * 10}%`, backgroundColor: color }}
        />
      </div>
      <span className="font-mono text-xs w-4 text-right" style={{ color }}>{value}</span>
    </div>
  );

  return (
    <div className="animate-fade-in flex flex-col lg:flex-row h-[calc(100vh-48px)]">
      {/* Left — Intelligence Panel */}
      <ScrollArea className="lg:w-[55%] border-r border-[rgba(255,255,255,0.06)]">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <Link to="/opportunities" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <SaturationBadge level={opportunity.saturationLevel} size="md" />
                <TierBadge score={opportunity.compositeScore} />
                {opportunity.confidence && (
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                    {opportunity.confidence} confidence
                  </span>
                )}
              </div>
              <h1 className="text-xl font-semibold text-foreground">{displayName}</h1>
              {opportunity.elevatorPitch && (
                <p className="text-sm text-muted-foreground mt-1">{opportunity.elevatorPitch}</p>
              )}
              <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[#5A6A68]">
                {opportunity.industry && <span className="uppercase">{opportunity.industry}</span>}
                {opportunity.signalCount && (
                  <>
                    <span>&middot;</span>
                    <span className="font-mono">{opportunity.signalCount} signals</span>
                  </>
                )}
                {opportunity.sourcePlatforms && opportunity.sourcePlatforms.length > 0 && (
                  <>
                    <span>&middot;</span>
                    <span className="font-mono">{opportunity.sourcePlatforms.length} sources</span>
                  </>
                )}
              </div>
            </div>
            <ScoreBadge score={opportunity.compositeScore} size="lg" />
          </div>

          {/* V2 Summary Cards */}
          {(opportunity.painSummary || opportunity.proposedSolution) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {opportunity.painSummary && (
                <div className="glass-card rounded-lg p-3">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold block mb-1">Pain</span>
                  <p className="text-sm text-foreground">{opportunity.painSummary}</p>
                </div>
              )}
              {opportunity.proposedSolution && (
                <div className="glass-card rounded-lg p-3">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold block mb-1">Solution</span>
                  <p className="text-sm text-foreground">{opportunity.proposedSolution}</p>
                </div>
              )}
            </div>
          )}

          {/* Competitors */}
          {competitors.length > 0 && (
            <div className="mb-6">
              <h2 className="font-brand text-sm tracking-wide mb-2">COMPETITORS</h2>
              <div className="flex flex-wrap gap-2">
                {competitors.map((comp, i) => (
                  <div
                    key={i}
                    className="bg-[rgba(255,255,255,0.04)] rounded-lg px-3 py-1.5 border border-[rgba(255,255,255,0.06)]"
                  >
                    <span className="text-xs font-medium text-foreground">{comp.name}</span>
                    <span className="text-[10px] text-electric-yellow ml-2">{comp.weakness}</span>
                  </div>
                ))}
              </div>
              {opportunity.blueOceanAngle && (
                <p className="text-xs text-mint mt-2 font-mono">
                  Blue Ocean: {opportunity.blueOceanAngle}
                </p>
              )}
            </div>
          )}

          {/* Status control */}
          <div className="flex items-center gap-3 mb-6">
            <Select defaultValue={opportunity.status}>
              <SelectTrigger className="w-[180px] h-8 text-xs bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0C0C0C] border-[rgba(255,255,255,0.08)]">
                <SelectItem value="discovered">Discovered</SelectItem>
                <SelectItem value="deep_dive">Deep Dive</SelectItem>
                <SelectItem value="building">Building</SelectItem>
                <SelectItem value="launched">Launched</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="text-xs border-[rgba(255,255,255,0.08)] text-muted-foreground">
              Link to Venture
            </Button>
          </div>

          {/* Evidence Panel */}
          <EvidencePanel opportunityId={id!} />

          {/* On-Demand Deep Dive Sections */}
          <DeepDiveSections opportunityId={id!} />

          {/* Saturation Assessment */}
          {opportunity.saturationLevel && (
            <div className="mb-6">
              <h2 className="font-brand text-lg tracking-wide mb-3">MARKET SATURATION</h2>
              <div className="glass-card rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <SaturationBadge level={opportunity.saturationLevel} size="md" />
                  <span className="text-sm text-foreground font-medium uppercase">
                    {opportunity.saturationLevel?.replace("_", " ")}
                  </span>
                </div>
                {opportunity.saturationDetail && (
                  <p className="text-sm text-muted-foreground mb-3">{opportunity.saturationDetail}</p>
                )}
                {competitors.length > 0 && (
                  <div className="space-y-2 mb-3">
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Known competitors:</span>
                    {competitors.map((comp, i) => (
                      <div key={i} className="pl-3 border-l-2 border-[rgba(255,255,255,0.08)]">
                        <span className="text-xs font-medium text-foreground">{comp.name}</span>
                        {comp.weakness && (
                          <span className="text-[11px] text-electric-yellow ml-2">— {comp.weakness}</span>
                        )}
                        {comp.pricing && (
                          <span className="text-[10px] text-muted-foreground ml-2">
                            {comp.pricing}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {opportunity.blueOceanAngle && (
                  <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.06)]">
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold block mb-1">Opening</span>
                    <p className="text-sm text-mint font-mono">{opportunity.blueOceanAngle}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Score Breakdown */}
          <div className="mb-6">
            <h2 className="font-brand text-lg tracking-wide mb-3">SCORE BREAKDOWN</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">MARKET QUALITY (60%)</span>
                <div className="mt-2 space-y-2">
                  {MARKET_METRICS.map((m) => (
                    <div key={m.key} className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider w-28 shrink-0">{m.label}</span>
                      {editingMetric === m.key ? (
                        <div className="flex-1 flex items-center gap-2">
                          <Slider
                            defaultValue={[opportunity[m.key]]}
                            max={10}
                            step={1}
                            className="flex-1"
                            onValueCommit={() => setEditingMetric(null)}
                          />
                        </div>
                      ) : (
                        <button className="flex-1 flex" onClick={() => setEditingMetric(m.key)}>
                          {renderScoreBar(opportunity[m.key], "#4B75F2")}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">YOUR ADVANTAGE (40%)</span>
                <div className="mt-2 space-y-2">
                  {ADVANTAGE_METRICS.map((m) => (
                    <div key={m.key} className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider w-28 shrink-0">{m.label}</span>
                      {editingMetric === m.key ? (
                        <div className="flex-1 flex items-center gap-2">
                          <Slider
                            defaultValue={[opportunity[m.key]]}
                            max={10}
                            step={1}
                            className="flex-1"
                            onValueCommit={() => setEditingMetric(null)}
                          />
                        </div>
                      ) : (
                        <button className="flex-1 flex" onClick={() => setEditingMetric(m.key)}>
                          {renderScoreBar(opportunity[m.key], "#44F2D2")}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* AI Analysis */}
          {opportunity.aiAnalysis && (
            <div className="mb-6">
              <h2 className="font-brand text-lg tracking-wide mb-3">AI ANALYSIS</h2>
              <div className="glass-card rounded-lg p-3">
                <p className="text-sm text-mint/90 font-mono leading-relaxed">{opportunity.aiAnalysis}</p>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Right — Strangelove Chat */}
      <div className="lg:w-[45%] flex flex-col bg-black relative scanlines">
        <div className="px-4 py-3 border-b border-[rgba(255,255,255,0.06)] flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-mint" />
          <span className="font-brand text-sm tracking-wider">STRANGELOVE DEEP DIVE</span>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-4">
            {localMessages.map((msg) => (
              <div
                key={msg.id}
                className={cn(
                  "rounded-lg px-3 py-2 max-w-[90%]",
                  msg.role === "commander"
                    ? "bg-[rgba(255,255,255,0.06)] ml-auto"
                    : "bg-[rgba(255,255,255,0.03)]"
                )}
              >
                <span className={cn(
                  "text-[10px] uppercase tracking-wider font-semibold block mb-1",
                  msg.role === "commander" ? "text-muted-foreground" : "text-mint"
                )}>
                  {msg.role === "commander" ? "COMMANDER" : "STRANGELOVE"}
                </span>
                <p className={cn(
                  "text-sm whitespace-pre-wrap leading-relaxed",
                  msg.role === "commander"
                    ? "text-foreground font-body"
                    : "text-mint/90 font-mono text-[13px]"
                )}>
                  {msg.content}
                </p>
              </div>
            ))}
            {sending && (
              <div className="rounded-lg px-3 py-2 max-w-[90%] bg-[rgba(255,255,255,0.03)]">
                <span className="text-[10px] uppercase tracking-wider font-semibold block mb-1 text-mint">
                  STRANGELOVE
                </span>
                <div className="flex items-center gap-2 text-mint/60 font-mono text-[13px]">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Strangelove is thinking...</span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        </ScrollArea>

        {/* Guided prompts */}
        <div className="px-4 py-2 border-t border-[rgba(255,255,255,0.04)]">
          <span className="text-[9px] text-muted-foreground uppercase tracking-wider block mb-1.5">Strategic Frameworks</span>
          <div className="flex gap-1.5 flex-wrap mb-2">
            {STRATEGIC_PROMPTS.map((prompt) => (
              <Button
                key={prompt.label}
                variant="outline"
                size="sm"
                className="text-[10px] border-electric-blue/30 text-electric-blue hover:bg-electric-blue/10 h-6 px-2"
                onClick={() => handleSend(prompt.label)}
                disabled={sending}
              >
                <prompt.icon className="w-3 h-3 mr-1" />
                {prompt.label}
              </Button>
            ))}
          </div>
          <span className="text-[9px] text-muted-foreground uppercase tracking-wider block mb-1.5">Actions</span>
          <div className="flex gap-1.5 flex-wrap">
            {ACTION_PROMPTS.map((prompt) => (
              <Button
                key={prompt.label}
                variant="outline"
                size="sm"
                className="text-[10px] border-mint/30 text-mint hover:bg-mint/10 h-6 px-2"
                onClick={() => handleSend(prompt.label)}
                disabled={sending}
              >
                <prompt.icon className="w-3 h-3 mr-1" />
                {prompt.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-[rgba(255,255,255,0.06)] flex gap-2">
          <Input
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            placeholder="Ask Strangelove..."
            disabled={sending}
            className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] text-foreground placeholder:text-[#555] focus:border-electric-blue focus:ring-electric-blue/20"
          />
          <Button
            onClick={() => handleSend()}
            size="icon"
            className="bg-electric-blue hover:bg-electric-blue/90 shrink-0"
            disabled={sending}
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
